/**
 * LiftCloud refuses with 409 a write that would put an uploaded MCE mapping on a
 * server that cannot read one, and names those servers in `servers`. Any bank,
 * channel or adapter write can meet it, so every path shows this one sentence.
 *
 * A refusal that names no servers says why in `code`: one that a reload cannot
 * clear must not read like a 409 that a reload can.
 */

/** What to tell the person for each `code` LiftCloud sends with a refusal. */
const CODE_MESSAGES = new Map<string, string>([
  [
    "mce_scope_old_cloud",
    "This adapter cannot be saved here until this site has synced with its cloud and the cloud has confirmed it can read uploaded MCE adapters. Connect the site to its cloud and try again.",
  ],
  [
    "mce_downgrade",
    "This adapter's mapping was built from an uploaded IDL and cannot be replaced with a legacy one. Upload the files again to change it.",
  ],
]);

/** The servers a refusal names, from an error's `data` or a parsed body. */
export function refusedServersIn(data: unknown): string[] {
  if (typeof data !== "object" || data === null || !("servers" in data)) return [];
  const { servers } = data;
  return Array.isArray(servers) ? servers.map(String) : [];
}

/** One plain sentence naming the servers that must be updated first. */
export function serversMessage(servers: string[]): string {
  return `This adapter cannot be sent to ${servers.join(", ")} until ${
    servers.length === 1 ? "that server is" : "those servers are"
  } updated. Update ${servers.length === 1 ? "it" : "them"} and try again.`;
}

/** The one plain sentence for a refusal, from an error's `data` or a parsed body; null for anything else. */
export function refusalMessage(data: unknown): string | null {
  const servers = refusedServersIn(data);
  if (servers.length > 0) return serversMessage(servers);
  if (typeof data !== "object" || data === null || !("code" in data)) return null;
  return CODE_MESSAGES.get(String(data.code)) ?? null;
}

/** An RTK error with its message replaced when it is a scope refusal; otherwise the same error. */
export function withScopeRefusalMessage<E>(error: E): E {
  if (typeof error !== "object" || error === null || !("data" in error)) return error;
  const message = refusalMessage(error.data);
  if (message === null) return error;
  return { ...error, data: { ...(error.data as object), message } };
}
